// Server functions for AI operations - creates jobs for job-processor
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { db } from '@/src/server/db'
import { z } from 'zod'
import crypto from 'crypto'
import { generateShareId } from '@/src/server/lib/share'
import { logger } from '@/src/server/lib/logger'
import { auth } from '@/auth'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import { checkRateLimit, recordRateLimitEvent, getUserIdentifier, extractAnonIdFromRequest } from '@/src/server/lib/rateLimit'
import { getFileKey } from '@/shared/lib/s3Keys'
import { enqueueJob } from '@/src/server/lib/redis'
import { getObjectMetadata, getPresignedUploadUrl } from '@/src/server/lib/s3/presigned'
import { PutObjectCommand } from '@aws-sdk/client-s3'
import { S3_BUCKET_NAME, s3Client as r2Client } from '@/src/server/lib/s3'

// Lightweight API input schemas (co-located to keep API contract local)
const AnalyzeFileSchema = z.object({
  name: z.string(),
  type: z.string(),
  data: z.string().optional(),
  hash: z.string(),
})

const AnalyzeInputSchema = z.object({
  files: z.array(AnalyzeFileSchema),
  promptId: z.string(),
  aiModelId: z.string(),
  storeData: z.boolean().default(true),
  fileHandling: z.enum(['single', 'multiple']).default('multiple'),
})

type AnalyzeInput = z.infer<typeof AnalyzeInputSchema>

const PrepareAnalyzeFileSchema = z.object({
  name: z.string(),
  type: z.string(),
  hash: z.string(),
})

const PrepareAnalyzeInputSchema = z.object({
  files: z.array(PrepareAnalyzeFileSchema),
})

const FinalizeAnalyzeFileSchema = z.object({
  fileId: z.string(),
  hash: z.string(),
})

const FinalizeAnalyzeInputSchema = z.object({
  files: z.array(FinalizeAnalyzeFileSchema),
  promptId: z.string(),
  aiModelId: z.string(),
  storeData: z.boolean().default(true),
  fileHandling: z.enum(['single', 'multiple']).default('multiple'),
})

type PreparedUploadFile = {
  fileId: string
  fileName: string
  hash: string
  contentType: string
  rawKey: string
  requiresUpload: boolean
  uploadUrl?: string
}

async function ensureAnalyzeRateLimit(userId?: string): Promise<void> {
  if (userId) return

  const request = getRequest()
  const anonId = request ? extractAnonIdFromRequest(request) : undefined
  const identifier = getUserIdentifier(null, anonId)
  const rateLimitCheck = await checkRateLimit(identifier, 'analyzeImage')

  if (!rateLimitCheck.allowed) {
    throw new Error(`Rate limit exceeded. Please try again after ${rateLimitCheck.resetAt.toLocaleTimeString()}.`)
  }

  await recordRateLimitEvent(identifier, 'analyzeImage')
}

async function validateAnalyzeDependencies(promptId: string, aiModelId: string): Promise<void> {
  const prompt = await db.prompt.findUnique({ where: { id: promptId } })
  if (!prompt || !prompt.prompt) {
    throw new Error(`Prompt with id '${promptId}' not found.`)
  }

  const aiModel = await db.aiModel.findUnique({ where: { id: aiModelId } })
  if (!aiModel || !aiModel.enabled) {
    throw new Error(`AI model with id '${aiModelId}' not found or disabled.`)
  }
}

async function ensureUserFileLink(fileId: string, userId?: string): Promise<void> {
  if (!userId) return

  const existing = await db.userFile.findFirst({ where: { fileId, userId } })
  if (existing) {
    await db.userFile.update({
      where: { id: existing.id },
      data: { deletedAt: null },
    })
    return
  }

  const user = await db.user.findUnique({
    where: { id: userId },
    select: { organizationId: true },
  })

  await db.userFile.create({
    data: {
      fileId,
      userId,
      organizationId: user?.organizationId,
    },
  })
}

export const prepareAnalysisUploads = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => PrepareAnalyzeInputSchema.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id

    await ensureAnalyzeRateLimit(userId)

    if (!data.files.length) {
      throw new Error('At least one file is required.')
    }

    const preparedFiles = await Promise.all(data.files.map(async (file): Promise<PreparedUploadFile> => {
      let fileRecord = await db.file.findFirst({ where: { hash: file.hash } })

      if (!fileRecord) {
        const shareId = await generateShareId({
          prefix: 'F',
          length: 8,
          isIdAvailable: async (id) => !(await db.file.findUnique({ where: { shareId: id } })),
        })

        fileRecord = await db.file.create({
          data: {
            shareId,
            name: file.name,
            type: file.type,
            hash: file.hash,
          },
        })
      }

      const rawKey = getFileKey(fileRecord.id, fileRecord.name)
      const metadata = await getObjectMetadata(rawKey)

      if (metadata) {
        return {
          fileId: fileRecord.id,
          fileName: fileRecord.name,
          hash: file.hash,
          contentType: metadata.contentType ?? fileRecord.type ?? file.type,
          rawKey,
          requiresUpload: false,
        }
      }

      const uploadUrl = await getPresignedUploadUrl(rawKey, 1800, file.type)

      return {
        fileId: fileRecord.id,
        fileName: fileRecord.name,
        hash: file.hash,
        contentType: file.type,
        rawKey,
        requiresUpload: true,
        uploadUrl,
      }
    }))

    return preparedFiles
  })

export const finalizeAnalysisUploads = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => FinalizeAnalyzeInputSchema.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const { files, promptId, aiModelId, storeData = true, fileHandling = 'multiple' } = data

    await ensureAnalyzeRateLimit(userId)
    await validateAnalyzeDependencies(promptId, aiModelId)

    if (!files.length) {
      throw new Error('At least one file is required.')
    }

    const finalizedFiles = await Promise.all(files.map(async (inputFile) => {
      const fileRecord = await db.file.findUnique({ where: { id: inputFile.fileId } })
      if (!fileRecord) {
        throw new Error('Prepared file record not found.')
      }

      if (fileRecord.hash && fileRecord.hash !== inputFile.hash) {
        throw new Error(`Hash mismatch for file ${fileRecord.name}.`)
      }

      const rawKey = getFileKey(fileRecord.id, fileRecord.name)
      const metadata = await getObjectMetadata(rawKey)
      if (!metadata) {
        throw new Error(`Upload missing for ${fileRecord.name}.`)
      }

      await ensureUserFileLink(fileRecord.id, userId)

      return {
        id: fileRecord.id,
        name: fileRecord.name,
        type: fileRecord.type,
        hash: inputFile.hash,
        size: metadata.size ?? 0,
        contentType: metadata.contentType ?? fileRecord.type ?? 'application/pdf',
      }
    }))

    if (fileHandling === 'single') {
      const { getOrCreateDrawingMaster } = await import('@/src/server/lib/drawingMaster')
      const masterResult = await getOrCreateDrawingMaster(finalizedFiles[0].id, { aiModelId })

      const drawingShareId = await generateShareId({
        prefix: 'D',
        length: 8,
        isIdAvailable: async (id) => !(await db.drawing.findUnique({ where: { shareId: id } })),
      })

      const drawing = await db.drawing.create({
        data: {
          shareId: drawingShareId,
          name: '',
          number: '',
          status: 'PROCESSING',
          masterId: masterResult.master.id,
          files: { connect: finalizedFiles.map(file => ({ id: file.id })) },
          userId,
        },
      })

      const primaryFile = finalizedFiles[0]
      const jobId = crypto.randomUUID()
      const rawKey = getFileKey(primaryFile.id, primaryFile.name)

      await db.job.create({
        data: {
          id: jobId,
          source: 'web',
          status: 'UPLOADED',
          originalFilename: primaryFile.name,
          contentType: primaryFile.contentType,
          size: primaryFile.size,
          rawKey,
          promptId,
          aiModelId,
          drawingId: drawing.id,
          fileId: primaryFile.id,
          userId,
          fileHandling: 'single',
        },
      })

      try {
        await enqueueJob(jobId)
      } catch (redisError) {
        logger.error({ error: redisError, jobId }, 'Failed to enqueue job to Redis')
      }

      return {
        data: { drawingId: drawing.id, jobId, fileId: primaryFile.id, fileName: primaryFile.name, status: 'PROCESSING' },
        meta: { fileHandling: 'single', storeData },
      }
    }

    const { getOrCreateDrawingMaster } = await import('@/src/server/lib/drawingMaster')
    const created = await Promise.all(finalizedFiles.map(async (file) => {
      const masterResult = await getOrCreateDrawingMaster(file.id, { aiModelId })

      const drawingShareId = await generateShareId({
        prefix: 'D',
        length: 8,
        isIdAvailable: async (id) => !(await db.drawing.findUnique({ where: { shareId: id } })),
      })

      const drawing = await db.drawing.create({
        data: {
          shareId: drawingShareId,
          name: '',
          number: '',
          status: 'PROCESSING',
          masterId: masterResult.master.id,
          files: { connect: [{ id: file.id }] },
          userId,
        },
      })

      const jobId = crypto.randomUUID()
      const rawKey = getFileKey(file.id, file.name)

      await db.job.create({
        data: {
          id: jobId,
          source: 'web',
          status: 'UPLOADED',
          originalFilename: file.name,
          contentType: file.contentType,
          size: file.size,
          rawKey,
          promptId,
          aiModelId,
          drawingId: drawing.id,
          fileId: file.id,
          userId,
          fileHandling: 'multiple',
        },
      })

      try {
        await enqueueJob(jobId)
      } catch (redisError) {
        logger.error({ error: redisError, jobId }, 'Failed to enqueue job to Redis')
      }

      return { drawingId: drawing.id, jobId, fileId: file.id, fileName: file.name }
    }))

    return {
      data: created,
      meta: { fileHandling: 'multiple', totalFiles: created.length, storeData },
    }
  })

const ReanalyzeFileInputSchema = z.object({
  fileShareId: z.string(),
  promptId: z.string(),
  aiModelId: z.string(),
})

/**
 * Create analysis jobs for job-processor using /api/jobs endpoint
 * 1. Creates File records and uploads to uploads/raw/${jobId}/
 * 2. Creates Job records with fileId reference
 * 3. Job processor processes files and updates File records with processed paths
 */
const analyzeDrawingData = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => AnalyzeInputSchema.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const { files, promptId, aiModelId, storeData = true, fileHandling = 'multiple' } = data

    // Check rate limit for anonymous users
    if (!userId) {
      const request = getRequest()
      const anonId = request ? extractAnonIdFromRequest(request) : undefined
      const identifier = getUserIdentifier(session, anonId)
      
      const rateLimitCheck = await checkRateLimit(identifier, 'analyzeImage')
      
      if (!rateLimitCheck.allowed) {
        throw new Error(`Rate limit exceeded. Please try again after ${rateLimitCheck.resetAt.toLocaleTimeString()}.`)
      }
      
      await recordRateLimitEvent(identifier, 'analyzeImage')
    }

    logger.debug({ fileHandling }, 'Creating analysis jobs for job-processor')

    // Fetch the prompt
    const prompt = await db.prompt.findUnique({ where: { id: promptId } })
    if (!prompt || !prompt.prompt) {
      throw new Error(`Prompt with id '${promptId}' not found.`)
    }

    // Fetch the AI model
    const aiModel = await db.aiModel.findUnique({ where: { id: aiModelId } })
    if (!aiModel || !aiModel.enabled) {
      throw new Error(`AI model with id '${aiModelId}' not found or disabled.`)
    }

    if (!files || !files.length) {
      throw new Error('At least one file is required.')
    }

    // Process each file: create Job → upload to rawKey → create File record → update Job with fileId
    const uploadedFiles = await Promise.all(files.map(async (file) => {
      // Use provided hash (frontend already computed it)
      const fileHash = file.hash

      // Check if file already exists by hash
      let fileRecord = await db.file.findFirst({ where: { hash: fileHash } })
      let fileSize = 0
      let fileContentType = file.type

      if (!fileRecord) {
        // New file - require data to upload
        if (!file.data) {
          throw new Error('File data is required for new files')
        }

        const buffer = Buffer.from(file.data, 'base64')

        // Create File record first to get fileId
        const shareId = await generateShareId({
          prefix: 'F',
          length: 8,
          isIdAvailable: async (id) => !(await db.file.findUnique({ where: { shareId: id } })),
        })

        fileRecord = await db.file.create({
          data: {
            shareId,
            name: file.name,
            type: file.type,
            hash: fileHash,
          },
        })

        // Upload file to fileId-based key with error handling
        const rawKey = getFileKey(fileRecord.id, file.name)
        
        if (!S3_BUCKET_NAME) {
          // Rollback: delete the File record we just created
          await db.file.delete({ where: { id: fileRecord.id } })
          throw new Error('S3_BUCKET_NAME not configured - cannot upload files')
        }

        try {
          // Upload to R2
          await r2Client.send(new PutObjectCommand({
            Bucket: S3_BUCKET_NAME,
            Key: rawKey,
            Body: buffer,
            ContentType: file.type,
          }))

          // Verify upload succeeded with HEAD request
          const metadata = await getObjectMetadata(rawKey)
          if (!metadata) {
            throw new Error(`Upload verification failed: file not found at ${rawKey}`)
          }

          fileSize = metadata.size ?? 0
          fileContentType = metadata.contentType ?? file.type

          logger.info(
            { fileId: fileRecord.id, rawKey, size: metadata.size },
            'File uploaded to R2 successfully'
          )
        } catch (uploadError) {
          // Rollback: delete the File record since upload failed
          await db.file.delete({ where: { id: fileRecord.id } })
          
          const errorMsg = uploadError instanceof Error ? uploadError.message : 'Unknown error'
          logger.error(
            { error: uploadError, fileId: fileRecord.id, rawKey, fileName: file.name },
            'Failed to upload file to R2, rolled back File record'
          )
          throw new Error(`Failed to upload ${file.name} to storage: ${errorMsg}`)
        }
      } else {
        // File already exists, get metadata for Job creation
        const rawKey = getFileKey(fileRecord.id, fileRecord.name)
        const metadata = await getObjectMetadata(rawKey)
        
        if (metadata) {
          fileSize = metadata.size ?? 0
          fileContentType = metadata.contentType ?? (fileRecord.type || 'application/pdf')
        }
      }

      // Create UserFile association if user is logged in
      // Always create UserFile for logged-in users, regardless of storeData flag
      // (storeData controls drawing visibility, but file should always be linked)
      if (userId) {
        const existing = await db.userFile.findFirst({ 
          where: { fileId: fileRecord.id, userId } 
        })
        if (existing) {
          await db.userFile.update({ 
            where: { id: existing.id }, 
            data: { deletedAt: null } 
          })
        } else {
          // Get user's organizationId to associate with this file
          const user = await db.user.findUnique({
            where: { id: userId },
            select: { organizationId: true },
          })
          await db.userFile.create({ 
            data: { fileId: fileRecord.id, userId, organizationId: user?.organizationId } 
          })
        }
      }

      return {
        id: fileRecord.id,
        name: fileRecord.name,
        type: fileRecord.type,
        hash: fileHash,
        size: fileSize,
        contentType: fileContentType,
      }
    }))

    // Create Drawing and Job records
    if (fileHandling === 'single') {
      // Create one drawing and one job for all files
      // First, create or get DrawingMaster for the primary file
      const { getOrCreateDrawingMaster } = await import('@/src/server/lib/drawingMaster')
      const masterResult = await getOrCreateDrawingMaster(uploadedFiles[0].id, { aiModelId })
      
      const drawingShareId = await generateShareId({
        prefix: 'D',
        length: 8,
        isIdAvailable: async (id) => !(await db.drawing.findUnique({ where: { shareId: id } })),
      })

      const drawing = await db.drawing.create({
        data: {
          shareId: drawingShareId,
          name: '',
          number: '',
          status: 'PROCESSING',
          masterId: masterResult.master.id,
          files: { connect: uploadedFiles.map(f => ({ id: f.id })) },
          userId,
        },
      })

      // Create Job for job-processor with fileId
      const jobId = crypto.randomUUID()
      const rawKey = getFileKey(uploadedFiles[0].id, uploadedFiles[0].name)
      
      await db.job.create({
        data: {
          id: jobId,
          source: 'web',
          status: 'UPLOADED',
          originalFilename: uploadedFiles[0].name,
          contentType: uploadedFiles[0].contentType,
          size: uploadedFiles[0].size,
          rawKey,
          promptId,
          aiModelId,
          drawingId: drawing.id,
          fileId: uploadedFiles[0].id,
          userId,
          fileHandling: 'single',
        },
      })

      logger.info({ jobId, drawingId: drawing.id, fileId: uploadedFiles[0].id }, 'Created analysis job for job-processor')

      // Enqueue to Redis
      try {
        await enqueueJob(jobId)
      } catch (redisError) {
        logger.error({ error: redisError, jobId }, 'Failed to enqueue job to Redis')
      }

      return { 
        data: { drawingId: drawing.id, jobId, status: 'PROCESSING' }, 
        meta: { fileHandling: 'single' } 
      }
    } else {
      // Create one drawing and one job per file
      const { getOrCreateDrawingMaster } = await import('@/src/server/lib/drawingMaster')
      
      const created = await Promise.all(uploadedFiles.map(async (file) => {
        // Create or get DrawingMaster for this file
        const masterResult = await getOrCreateDrawingMaster(file.id, { aiModelId })
        
        const drawingShareId = await generateShareId({
          prefix: 'D',
          length: 8,
          isIdAvailable: async (id) => !(await db.drawing.findUnique({ where: { shareId: id } })),
        })

        const drawing = await db.drawing.create({
          data: {
            shareId: drawingShareId,
            name: '',
            number: '',
            status: 'PROCESSING',
            masterId: masterResult.master.id,
            files: { connect: [{ id: file.id }] },
            userId,
          },
        })

        // Create Job for job-processor with fileId
        const jobId = crypto.randomUUID()
        const rawKey = getFileKey(file.id, file.name)
        
        await db.job.create({
          data: {
            id: jobId,
            source: 'web',
            status: 'UPLOADED',
            originalFilename: file.name,
            contentType: file.contentType,
            size: file.size,
            rawKey,
            promptId,
            aiModelId,
            drawingId: drawing.id,
            fileId: file.id,
            userId,
            fileHandling: 'multiple',
          },
        })

        logger.info({ jobId, drawingId: drawing.id, fileId: file.id }, 'Created analysis job for job-processor')

        // Enqueue to Redis
        try {
          await enqueueJob(jobId)
        } catch (redisError) {
          logger.error({ error: redisError, jobId }, 'Failed to enqueue job to Redis')
        }

        return { drawingId: drawing.id, jobId, fileId: file.id, fileName: file.name }
      }))

      return { 
        data: created, 
        meta: { fileHandling: 'multiple', totalFiles: created.length } 
      }
    }
  })

export const getAiModels = createServerFn({ method: 'GET' })
  .handler(async () => {
    const models = await db.aiModel.findMany({
      where: { enabled: true },
      orderBy: [{ isDefault: 'desc' }, { name: 'asc' }],
    })
    return models.map((m: any) => ({
      ...m,
      createdAt: m.createdAt.toISOString(),
      updatedAt: m.updatedAt.toISOString(),
    }))
  })

export const setDefaultAiModel = createServerFn({ method: 'POST' })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    // First, unset any existing default models
    await db.aiModel.updateMany({
      where: { isDefault: true },
      data: { isDefault: false },
    })
    
    // Then set the new default
    const model = await db.aiModel.update({
      where: { id: data.id },
      data: { isDefault: true },
    })
    
    return model
  })

export const getDefaultAiModel = createServerFn({ method: 'GET' })
  .handler(async () => {
    const model = await db.aiModel.findFirst({
      where: { isDefault: true, enabled: true },
      orderBy: { createdAt: 'asc' },
    })
    
    // If no default is set, return the first enabled model
    if (!model) {
      return await db.aiModel.findFirst({
        where: { enabled: true },
        orderBy: { createdAt: 'asc' },
      })
    }
    
    return model
  })

export const reanalyzeFile = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => ReanalyzeFileInputSchema.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const { fileShareId, promptId, aiModelId } = data

    // Check rate limit for anonymous users
    if (!userId) {
      const request = getRequest()
      const anonId = request ? extractAnonIdFromRequest(request) : undefined
      const identifier = getUserIdentifier(session, anonId)
      
      const rateLimitCheck = await checkRateLimit(identifier, 'analyzeImage')
      
      if (!rateLimitCheck.allowed) {
        throw new Error(`Rate limit exceeded. Please try again after ${rateLimitCheck.resetAt.toLocaleTimeString()}.`)
      }
      
      await recordRateLimitEvent(identifier, 'analyzeImage')
    }

    // Find the file by shareId
    const file = await (db as any).file.findFirst({
      where: { shareId: fileShareId },
      include: {
        userFiles: { where: { deletedAt: null } },
      },
    })

    if (!file) {
      throw new Error('File not found.')
    }

    // Check user has access to this file
    if (userId) {
      const hasAccess = file.userFiles.some((uf: any) => uf.userId === userId)
      if (!hasAccess) {
        throw new Error('Unauthorized.')
      }
    }

    // Fetch the prompt
    const prompt = await db.prompt.findUnique({ where: { id: promptId } })
    if (!prompt || !prompt.prompt) {
      throw new Error(`Prompt with id '${promptId}' not found.`)
    }

    // Fetch the AI model
    const aiModel = await db.aiModel.findUnique({ where: { id: aiModelId } })
    if (!aiModel || !aiModel.enabled) {
      throw new Error(`AI model with id '${aiModelId}' not found or disabled.`)
    }

    // Create a new drawing in PROCESSING state
    const drawingShareId = await generateShareId({
      prefix: 'D',
      length: 8,
      isIdAvailable: async (id) => !(await db.drawing.findUnique({ where: { shareId: id } })),
    })

    const drawing = await db.drawing.create({
      data: {
        shareId: drawingShareId,
        name: '',
        number: '',
        status: 'PROCESSING',
        files: { connect: [{ id: file.id }] },
        userId,
      },
    })

    // Create Job for job-processor with fileId reference
    const jobId = crypto.randomUUID()
    const rawKey = getFileKey(file.id, file.name) // Derive from fileId
    
    // Verify file exists in R2 before creating reanalysis job
    const metadata = await getObjectMetadata(rawKey)
    if (!metadata) {
      throw new Error(
        `Cannot reanalyze: File not found in R2 storage.\n` +
        `Expected location: ${rawKey}\n` +
        `This happens when the database contains file records but R2 storage is missing the actual files.\n` +
        `Solutions: (1) Re-upload the original file for fresh analysis, or (2) Restore R2 from backup.`
      )
    }
    
    await db.job.create({
      data: {
        id: jobId,
        source: 'web',
        status: 'UPLOADED',
        originalFilename: file.name,
        contentType: file.type || 'application/pdf',
        rawKey,
        promptId,
        aiModelId,
        drawingId: drawing.id,
        fileId: file.id,
        userId,
        fileHandling: 'single',
      },
    })

    logger.info({ jobId, drawingId: drawing.id, fileId: file.id }, 'Created reanalysis job for job-processor')

    // Enqueue to Redis for immediate processing
    const { enqueueJob } = await import('@/src/server/lib/redis')
    try {
      await enqueueJob(jobId)
    } catch (redisError) {
      logger.error({ error: redisError, jobId }, 'Failed to enqueue reanalysis job to Redis')
      // Job will still be picked up by fallback polling
    }

    return {
      drawingId: drawing.id,
      drawingShareId: drawing.shareId,
      jobId,
      status: 'PROCESSING',
    }
  })
